/**
 * EnrollmentReportPanel
 *
 * Members-by-program enrollment report with Program + Status filters, a
 * results table (Member, Program, Status, Enrolled date) and a client-side
 * CSV export. LCC-scoped for now (rendered only when activeOrgId === 350 by
 * ActivityReportsPage — the panel itself is org-agnostic and reusable).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardList, FileDown, Loader2 } from 'lucide-react';
import {
  enrollmentsAPI,
  Enrollment,
  EnrollmentProgram,
  EnrollmentStatus,
} from '@/services/api/enrollments';

const STATUS_OPTIONS: { value: EnrollmentStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'terminated', label: 'Terminated' },
];

const fmtDate = (iso?: string): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
};

export const EnrollmentReportPanel: React.FC = () => {
  const [programs, setPrograms] = useState<EnrollmentProgram[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<number | undefined>();
  const [selectedStatus, setSelectedStatus] = useState<EnrollmentStatus | undefined>();
  const [rows, setRows] = useState<Enrollment[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Program filter options
  useEffect(() => {
    enrollmentsAPI
      .getProgramsWithEnrollments()
      .then((r) => setPrograms(r.programs || []))
      .catch(() => setPrograms([]));
  }, []);

  // Load enrollments whenever a filter changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    enrollmentsAPI
      .listEnrollments({
        page: 1,
        page_size: 500,
        program_id: selectedProgram,
        status: selectedStatus,
      })
      .then((r) => {
        if (cancelled) return;
        setRows(r.enrollments || []);
        setTotal(r.total || (r.enrollments?.length ?? 0));
      })
      .catch((e: any) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e?.message || 'Failed to load enrollments');
        setRows([]);
        setTotal(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProgram, selectedStatus]);

  const programName = useMemo(() => {
    const map = new Map(programs.map((p) => [p.id, p.name]));
    return (e: Enrollment) => e.program_name || map.get(e.program_id) || `Program #${e.program_id}`;
  }, [programs]);

  const exportCsv = () => {
    const header = ['Member', 'Program', 'Status', 'Enrolled Date'];
    const escape = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = rows.map((e) =>
      [
        e.member_name || `Member #${e.member_id}`,
        programName(e),
        (e.enrollment_status || '').replace('_', ' '),
        e.enrollment_date ? fmtDate(e.enrollment_date) : '',
      ]
        .map(escape)
        .join(',')
    );
    const csv = [header.map(escape).join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `enrollments-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-700 p-4">
        <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-900 dark:text-gray-100">
          <ClipboardList className="w-5 h-5 text-blue-600" />
          Enrollments Report
        </h2>
        <button
          type="button"
          onClick={exportCsv}
          disabled={loading || rows.length === 0}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          <FileDown className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 p-4">
        <select
          value={selectedProgram?.toString() || '__all__'}
          onChange={(e) => setSelectedProgram(e.target.value !== '__all__' ? parseInt(e.target.value, 10) : undefined)}
          className="h-9 min-w-[200px] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 text-sm text-gray-900 dark:text-gray-100"
        >
          <option value="__all__">All Programs</option>
          {programs.map((p) => (
            <option key={p.id} value={p.id.toString()}>
              {p.name}
            </option>
          ))}
        </select>

        <select
          value={selectedStatus || '__all__'}
          onChange={(e) => setSelectedStatus(e.target.value !== '__all__' ? (e.target.value as EnrollmentStatus) : undefined)}
          className="h-9 min-w-[150px] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 text-sm text-gray-900 dark:text-gray-100"
        >
          <option value="__all__">All Statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        <span className="text-sm text-gray-500 dark:text-gray-400 ml-auto">
          {loading ? 'Loading…' : `${total} enrollment${total === 1 ? '' : 's'}`}
        </span>
      </div>

      {error && (
        <div className="mx-4 mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading enrollments…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
            No enrollments match the current filters.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-t border-b border-gray-100 dark:border-gray-700 text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                <th className="px-4 py-2 font-medium">Member</th>
                <th className="px-4 py-2 font-medium">Program</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Enrolled Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className="border-b border-gray-50 dark:border-gray-700/50">
                  <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                    {e.member_name || `Member #${e.member_id}`}
                  </td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{programName(e)}</td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300 capitalize">
                    {(e.enrollment_status || '').replace('_', ' ')}
                  </td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{fmtDate(e.enrollment_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};

export default EnrollmentReportPanel;
